/**
 * The tab shell (docs/ux/review/navigation-ia.md §3, artboards `optimal/`): the tab strip on top
 * is the only navigation — Home (⌥0), project tabs (⌥1–9), `+`, Search, Settings. Below it the
 * page, the right-hand inspector slot and, on desktop, the status line. Phones get the strip as
 * chips plus a bottom bar (MobileBar). No left rail.
 *
 *   ┌ tabs ─────────────────────────────────────────────┐
 *   │ page (Outlet)                    │ inspector slot │
 *   └ status line (desktop) / bottom bar (phone) ───────┘
 */
import { useRouterState } from '@tanstack/react-router'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useMaybeCall } from '../../lib/call/CallProvider.js' // viko-call
import { loadHidden, saveHidden } from '../../lib/explorer.js'
import { projectViewOfPath } from '../../lib/ia.js'
import { rememberPlace } from '../../lib/tab-memory.js'
import { CallDock } from '../call/CallDock.js' // viko-call
import { CallShowPane } from '../call/CallShowPane.js' // viko-call
import { MobileBar } from './MobileBar.js'
import { ShellTabs, useIde } from './ShellTabs.js'
import { StatusLine, useStatusLinePref } from './StatusLine.js'
import { ShellSlotsContext } from './slots.js'
import './shell.css'
import './explorer.css'

export function TabShell({ banners, children }: { banners: ReactNode; children: ReactNode }) {
  const [inspector, setInspector] = useState<HTMLElement | null>(null)
  const [statusActions, setStatusActions] = useState<HTMLElement | null>(null)
  const slots = useMemo(() => ({ inspector, statusActions }), [inspector, statusActions])
  const status = useStatusLinePref()
  const call = useMaybeCall() // viko-call
  const showing = !!call?.state.show && call.state.phase !== 'idle' && call.state.view !== 'min'
  // app-clarity: from 1280px the Explorer (sidebar tree) replaces the tab strip; ⌘B hides it.
  const ide = useIde()
  const [hidden, setHidden] = useState(loadHidden)
  const setSidebarHidden = (h: boolean) => {
    setHidden(h)
    saveHidden(h)
  }
  useEffect(() => {
    if (!ide) return
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'b') return
      e.preventDefault()
      setHidden((h) => {
        saveHidden(!h)
        return !h
      })
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [ide])
  // Each tab reopens where you were (lib/tab-memory.ts).
  const here = useRouterState({
    select: (s) => `${s.location.pathname}\u0000${s.location.searchStr}`,
  })
  useEffect(() => {
    const [path = '', search = ''] = here.split('\u0000')
    rememberPlace(path, search)
  }, [here])
  // A run's full conversation is a focused view on phones: no bottom bar (as before).
  const inRun = useRouterState({
    select: (s) =>
      projectViewOfPath(s.location.pathname) === 'run' || s.location.pathname.startsWith('/s/'),
  })
  return (
    <ShellSlotsContext.Provider value={slots}>
      <div
        className={`vs-shell${status ? ' has-status' : ''}${inRun ? ' in-run' : ''}${ide ? ' is-ide' : ''}${ide && hidden ? ' is-sidebar-hidden' : ''}`}
      >
        <a className="vs-skip" href="#main">
          Skip to content
        </a>
        <ShellTabs ide={ide} sidebarHidden={hidden} onSidebarHidden={setSidebarHidden} />
        <div className="vs-banners">{banners}</div>
        <div className="vs-body">
          <main id="main" className="vs-main" tabIndex={-1}>
            {/* viko-call: call.show takes the main pane; the page stays mounted underneath. */}
            {showing ? <CallShowPane /> : null}
            <div style={{ display: showing ? 'none' : 'contents' }}>{children}</div>
          </main>
          <div className="vs-inspector" data-slot="inspector" ref={setInspector} />
          {call && call.state.phase !== 'idle' ? <CallDock /> : null}
        </div>
        {status ? <StatusLine actionsRef={setStatusActions} /> : null}
        <MobileBar />
      </div>
    </ShellSlotsContext.Provider>
  )
}
